import { Component, OnInit, OnDestroy, Output, ViewChild, EventEmitter, Input } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import * as _ from 'lodash';
import { MenuItem, MessageService } from "primeng/api";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { TabViewModule } from "primeng/tabview";
import { AppSettings } from "../../../../../shared/app.settings";
import { MiFormComponent } from "../../../../../shared/components/mi-form/mi-form.component";
import type { Country } from "../../../../../shared/models/country";
import type { Language } from "../../../../../shared/models/language";
import { CommonBindingDataService } from "../../../../../shared/services/common-binding-data.service";
import { EntityService } from "../../../../../shared/services/entity.service";
import { ConfigService } from "app/modules/shared/services/config.service";
import { EntityResponse } from "app/modules/shared/models/entityResponse.models";
import { AttributeData } from "app/modules/shared/models/attribute.models";
import { UserManagementService } from "app/modules/user-management/services/user-management.service";
import { MetaDataService } from "app/modules/shared/services/meta-data.service";
import { SkeletonModule } from "primeng/skeleton";

interface UserData {
  userId: string;
  parentName: string;
  typeId: string;
  [key: string]: unknown;
}

interface SiteData {
  sponsor: Array<{
    entity_name: string;
    entityId: string;
  }>;
}

@Component({
  selector: 'app-add-protocol-patient',
  standalone: true,
  imports: [
    BreadcrumbModule,
    TabViewModule,
    MiFormComponent,
    DialogModule,
    ButtonModule,
    TranslateModule,
    SkeletonModule
  ],
  templateUrl: './add-patient.component.html',
  styleUrl: './add-patient.component.scss'
})
export class AddProtocolPatientComponent implements OnInit, OnDestroy {

  @ViewChild(MiFormComponent) miFormComponent: MiFormComponent;
  @Output() closePopup = new EventEmitter();
  @Input() patientId: string;
  @Input() protocolId: string;
  @Input() protocolDates: any = { startDate: null, endDate: null };
  data: AttributeData;
  patientAttributeData: AttributeData;
  routePath: MenuItem[] = [];
  attributeLabels: Record<string, string> = {};
  moduleName: string;
  country: Country;
  language: Language;
  relation: string = "oneToOne";
  cols: Array<{ field: string; header: string }> = [];
  patientAttributeLength: number;
  patientRelation: { id: string; name: string; entityRelationshipConfigId?: string };
  attributeValues: Record<string, unknown> = {};
  previousBtnLabel: string;
  label: string;
  savePatientBtnLabel: string;
  isNew: boolean = true;
  prefixToAttachForButtonIdToUniquelyIdentifyButton = AppSettings.BUTTON_PREFIX;
  tabCode: string;
  loadDialogView = false;
  patientHeader: string;
  successMessage: string;
  originalPatientAttributeData: AttributeData;
  patienSubmitBtn: string;


  constructor(
    private patientService: EntityService,
    private cs: CommonBindingDataService,
    private messageService: MessageService,
    private configService: ConfigService,
    private userService: UserManagementService,
    private metaDataService: MetaDataService
  ) { }

  ngOnInit() {
    this.initializeLabels();
    this.initializePatientService();
    this.setSiteNameForPatient();
  }

  setSiteNameForPatient() {
    const siteData: SiteData = { sponsor: [] };
    const profileData = JSON.parse(localStorage.getItem('profileData'));
    const loggedInUserId = profileData.userId;
    this.userService.getUserDetails(loggedInUserId).subscribe((userData: UserData) => {
      const dataArr = {
        entity_name: userData.parentName,
        entityId: userData.typeId
      };
      siteData.sponsor.push(dataArr);
      this.patientService.setRelatedData(siteData);

      if (this.patientId) {
        this.loadPatientDetails();
      } else {
        this.loadPatientData();
      }

    });
  }

  loadPatientDetails() {
    this.patientService.getEntity(this.patientId, AppSettings.ENTITY_CODE.PROTOCOL_PATIENT, AppSettings.VIEW_CODE.DETAIL_VIEW).subscribe((res: any) => {
      this.attributeValues = this.cs.getOrgAttributeValues(res) as unknown as Record<string, unknown>;
      this.normalizeStartEndDate();
      this.loadPatientData();
      if (this.patientId) {
        // Delay the form initialization and recalculation to ensure form is ready
        setTimeout(() => {
          this.initializeFormWithExistingValues();
          this.triggerNextVisitRecalculation();
        }, 200);
      }
    });
  }

  private normalizeStartEndDate() {
    const startEndDateCodes = ['commencement_date', 'next_visit', 'pause_from_date', 'pause_to_date'];
    for (const dateCode of startEndDateCodes) {
      if (this.attributeValues[dateCode]) {
        const updatedDate = this.metaDataService.setDateInTimezone(this.attributeValues[dateCode] as number);
        this.attributeValues[dateCode] = new Date(updatedDate as string | number);
      }
    }

    if (this.attributeValues.commencement_date) {
      this.cs.emitDateValue(this.attributeValues.commencement_date as Date);
    }

    if (this.attributeValues.visit_frequency) {
      this.cs.emitDaysValue(this.attributeValues.visit_frequency as number);
    }

    if (this.attributeValues.pause_from_date) {
      // Ensure pause dates are properly timezone-adjusted
      const pauseFromDate = this.attributeValues.pause_from_date as Date;
      pauseFromDate.setHours(0, 0, 0, 0);
      this.cs.emitPauseFromDate(pauseFromDate);
    }

    if (this.attributeValues.pause_to_date) {
      // Ensure pause dates are properly timezone-adjusted
      const pauseToDate = this.attributeValues.pause_to_date as Date;
      pauseToDate.setHours(0, 0, 0, 0);
      this.cs.emitPauseToDate(pauseToDate);
    }
  }

  private initializeLabels() {
    this.patientHeader = this.patientId ? this.cs.getLabel("patient.lbl_edit_patient") : this.cs.getLabel("patient.lbl_add_patient");
    this.patienSubmitBtn = this.patientId ? this.cs.getLabel("patient.lbl_update_patient") : this.cs.getLabel("patient.lbl_add_patient");
    this.successMessage = this.patientId ? this.cs.getLabel('patient.lbl_patient_updated_successfully') : this.cs.getLabel('patient.lbl_patient_added_successfully');
  }

  private initializePatientService() {
    this.moduleName = AppSettings.ENTITY_TYPE.PROTOCOL_PATIENT;
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
  }

  private loadPatientData() {
    this.getAttributes();
  }


  getAttributes() {
    const entityType = AppSettings.ENTITY_TYPE.PROTOCOL_PATIENT;
    const viewCode = AppSettings.VIEW_CODE.ADD_EDIT_VIEW;

    this.patientService.getAttributeDefinition(entityType, viewCode).subscribe(res => {
      if (!res) return;
      this.setPatientView(res);
    });
  }

  setPatientView(res: AttributeData) {
    this.loadDialogView = true;
    this.attributeLabels = this.cs.getAttributeLabels(res) as unknown as Record<string, string>;
    this.originalPatientAttributeData = this.cs.getOrganizedAttribute(res);
    this.patientAttributeData = _.cloneDeep(this.originalPatientAttributeData);
    this.patientAttributeData.tabs = this.patientAttributeData?.tabs.map(tab => ({
      ...tab,
      groups: tab.groups.map(group => ({
        ...group,
        fields: group.fields.map(field => {
          if (field.attributeCode === 'default_address') {
            return {
              ...field,
              presetValues: [{ labelKey: "Yes", labelValue: "Yes", parentLabelKey: null, color: null, iconUrl: null }]
            };
          }
          if (field.attributeCode === 'commencement_date') {
            const startDate = this.protocolDates.startDate as number;
            const endDate = this.protocolDates.endDate as number;

            if (startDate || endDate) {
              field.validation = {
                required: field.validation?.required || false,
                min: startDate || field.validation?.min || null,
                max: endDate || field.validation?.max || null,
                pattern: field.validation?.pattern || null,
                unique: field.validation?.unique || null
              };
            }
          }
          if (field.attributeCode === 'next_visit') {
            return {
              ...field,
              disabled: true
            };
          }
          if (field.attributeCode === 'patient_id' && this.patientId) {
            return {
              ...field,
              disabled: true
            };
          }
          return field;
        }).filter(field => field.attributeCode !== 'protocol_entity_id')
      }))
    }));
    this.patientAttributeData.tabs = this.cs.setDependsOnAttributeCode(this.patientAttributeData?.tabs);
    this.patientAttributeLength = this.patientAttributeData.tabs.length;
    this.patientRelation = _.find(res?.relations, { otherEntityCode: 'site', relation: "oneToOne" });


  }

  private initializeFormWithExistingValues() {
    if (this.miFormComponent && this.miFormComponent.miFormGroup) {
      const form = this.miFormComponent.miFormGroup;

      if (this.attributeValues.commencement_date && form.get('commencement_date')) {
        form.get('commencement_date').setValue(this.attributeValues.commencement_date);
      }

      if (this.attributeValues.visit_frequency && form.get('visit_frequency')) {
        form.get('visit_frequency').setValue(this.attributeValues.visit_frequency);
      }

      if (this.attributeValues.next_visit && form.get('next_visit')) {
        form.get('next_visit').setValue(this.attributeValues.next_visit);
      }

      if (this.attributeValues.pause_from_date && form.get('pause_from_date')) {
        form.get('pause_from_date').setValue(this.attributeValues.pause_from_date);
      }

      if (this.attributeValues.pause_to_date && form.get('pause_to_date')) {
        form.get('pause_to_date').setValue(this.attributeValues.pause_to_date);
      }
    }
  }

  private triggerNextVisitRecalculation() {

    if (this.attributeValues.commencement_date && this.attributeValues.visit_frequency) {
      this.cs.emitDateValue(this.attributeValues.commencement_date as Date);
      this.cs.emitDaysValue(this.attributeValues.visit_frequency as number);
    }

    // Also emit pause dates if they exist
    if (this.attributeValues.pause_from_date) {
      const pauseFromDate = this.attributeValues.pause_from_date as Date;
      pauseFromDate.setHours(0, 0, 0, 0);
      this.cs.emitPauseFromDate(pauseFromDate);
    }

    if (this.attributeValues.pause_to_date) {
      const pauseToDate = this.attributeValues.pause_to_date as Date;
      pauseToDate.setHours(0, 0, 0, 0);
      this.cs.emitPauseToDate(pauseToDate);
    }
  }



  onSavePatient(event) {
    const patientData = {
      forTenantCode: this.configService.getForTenantCode(),
      entityCode: AppSettings.ENTITY_CODE.PROTOCOL_PATIENT,
      countryCode: this.country[0].countryCode,
      languageCode: this.language[0].langCode,
      data: [],
      relationshipData: []
    };

    const pData = event;
    const dateIds = ["commencement_date", "next_visit"];
    for (const [key, value] of Object.entries(pData)) {
      let attributeValue = <any>value;
      if (dateIds.includes(key)) {
        const dateValue = this.metaDataService.getTimeZoneFormattedDate(value as Date);
        attributeValue = new Date(<string>dateValue).getTime();
        patientData.data.push({
          attributeCode: key,
          attributeValue
        });
      } else if (key === 'site_entity_id') {
        patientData?.relationshipData?.push({
          entityRelationshipConfigId: this.patientRelation.entityRelationshipConfigId,
          otherEntityId: value
        });
        patientData.data.push({
          attributeCode: key,
          attributeValue,
        });
      } else if (attributeValue || attributeValue?.length) {
        patientData.data.push({
          attributeCode: key,
          attributeValue
        });
      }
    }

    patientData.data.push({
      attributeCode: 'protocol_entity_id',
      attributeValue: this.protocolId
    });

    const entitiesData = {
      countryCode: this.country[0].countryCode,
      tenantCode: this.configService.getForTenantCode(),
    };
    if (!this.patientId) {
      this.patientService
        .createEntities(AppSettings.ENTITY_CODE.PROTOCOL_PATIENT, entitiesData)
        .subscribe((res: EntityResponse) => {
          this.patientId = res.entityId;
          this.savePatientData(patientData, true);
        },
        );

    } else {
      this.savePatientData(patientData, false);
    }
  }

  savePatientData(patientData, isNew: boolean) {
    this.isNew = isNew;
    patientData.data = this.cs.mapAttributeIds(patientData.data, this.originalPatientAttributeData.tabs)
    this.patientService
      .saveAttributeData(patientData.entityCode, this.patientId, patientData).subscribe({
        next: () => {
          this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.successMessage });
          setTimeout(() => {
            this.closeDialog(true);
          }, 50);
        },
        error: () => {
          if (isNew) {
            this.cs.onDisplayMessage(this.data, 'patient', AppSettings.MSG_ACTION.ADD, 'error');
          } else {
            this.cs.onDisplayMessage(this.data, 'patient', AppSettings.MSG_ACTION.UPDATE, 'error');
          }
        }
      });
  }

  closeDialog(event) {
    this.patientId = null;
    // Clear pause dates from service when dialog closes
    this.cs.clearPauseDates();
    this.closePopup.emit(event);
  }

  ngOnDestroy() {
    // Clear pause dates from service when component is destroyed
    this.cs.clearPauseDates();
  }
}
